/**
 * Cálculo CTS depósito (semestre) en cliente — alineado a CtsCalculoService + factor de tiempo:
 * (Rem. computable / 12) × min(6, meses_completos + días/30). MYPP (Pequeña Empresa): monto CTS × 1/2.
 * Factor tiempo CTS: **días naturales** (desde ingreso al fin del semestre, inclusive) en bloques de 30 días (treintena),
 * equivalente = min(6, días÷30); mismo criterio que el backend.
 * Lista de ingresos por mes y base: desde el primer mes con vínculo al cierre de ese mes.
 * El 1/6 de gratificación solo usa monto manual o sugerido (planilla/grat.); no hay gratificación proyectada. Base fija/variable alineada al backend
 * (últimos 3 iguales, moda única mayoritaria, etc.).
 * CTS **Mayo**: ingreso posterior al 01/04 del año del CTS; **Noviembre**: posterior al 01/10: no aplicar vista previa (misma política que el backend).
 */

export function getOrdenMesesCts(periodo, anio) {
  const y = Number(anio);
  if (!y || Number.isNaN(y)) return [];
  if (periodo === 'mayo') {
    return [`${y - 1}-11`, `${y - 1}-12`, `${y}-01`, `${y}-02`, `${y}-03`, `${y}-04`];
  }
  return [`${y}-05`, `${y}-06`, `${y}-07`, `${y}-08`, `${y}-09`, `${y}-10`];
}

/** Julio o diciembre dentro del semestre CTS (meses de pago de gratificación). */
export function semestreCtsIncluyeMesGratificacion(periodo, anio) {
  return getOrdenMesesCts(periodo, anio).some((ym) => {
    const mm = ym.slice(5, 7);
    return mm === '07' || mm === '12';
  });
}

function parseDateSafe(value) {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const head = value.slice(0, 10);
    const soloFecha = head.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (soloFecha) {
      const [, ys, ms, ds] = soloFecha;
      return new Date(Number(ys), Number(ms) - 1, Number(ds));
    }
  }
  return new Date(value);
}

function formatYmd(d) {
  if (!d || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Último día del mes Y-m como Y-m-d (comparable con fecha ingreso). */
function ultimoDiaYmString(ym) {
  const [ys, ms] = ym.split('-').map(Number);
  const d = new Date(ys, ms, 0);
  return formatYmd(d);
}

/**
 * Tiempo CTS: días naturales entre `desde` y `hasta` (inclusive) en treintenas de 30 días; tope `maxMeses` equivalentes
 * (p. ej. 6 → 180 días computables).
 */
function mesesEquivalentesCtsTrentina(desde, hasta, maxMeses = 6) {
  const dIni = parseDateSafe(desde);
  const dFin = parseDateSafe(hasta);
  if (Number.isNaN(dIni.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dIni) {
    return { meses: 0, dias: 0, equivalente: 0 };
  }
  const totalDias = Math.floor((dFin - dIni) / (24 * 60 * 60 * 1000)) + 1;
  const maxDiasEquiv = maxMeses * 30;
  const diasCts = Math.min(totalDias, maxDiasEquiv);
  const meses = Math.floor(diasCts / 30);
  const dias = diasCts % 30;
  const equivalente = Math.min(maxMeses, diasCts / 30);

  return { meses, dias, equivalente };
}

/**
 * CTS Mayo: ingreso &gt; 1 abr.; CTS Noviembre: ingreso &gt; 1 oct. del año (solo Y-m-d) → no figurar / null en live.
 */
export function ingresoExcluyePorPoliticaDepositoCts(periodo, anio, ultimaFechaIngresoLabores) {
  if (
    (periodo !== 'mayo' && periodo !== 'noviembre') ||
    ultimaFechaIngresoLabores == null ||
    String(ultimaFechaIngresoLabores).trim() === ''
  ) {
    return false;
  }
  const y = Number(anio);
  if (!y || Number.isNaN(y)) return false;
  const limStr = periodo === 'mayo' ? `${y}-04-01` : `${y}-10-01`;
  let ingStr;
  if (
    typeof ultimaFechaIngresoLabores === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(ultimaFechaIngresoLabores.trim())
  ) {
    ingStr = ultimaFechaIngresoLabores.trim();
  } else {
    ingStr = formatYmd(parseDateSafe(ultimaFechaIngresoLabores));
  }
  if (!ingStr) return false;
  return ingStr > limStr;
}

/** Primer mes del semestre con vínculo al cierre de ese mes (lista sueldos / base). Mismo criterio que el backend. */
export function indicePrimerMesComputoCts(ordenMeses, fechaIngreso) {
  if (!ordenMeses?.length) return 0;
  if (fechaIngreso == null || fechaIngreso === '') return 0;
  let ingStr;
  if (typeof fechaIngreso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaIngreso.trim())) {
    ingStr = fechaIngreso.trim();
  } else {
    ingStr = formatYmd(parseDateSafe(fechaIngreso));
  }
  for (let i = 0; i < ordenMeses.length; i += 1) {
    const ultStr = ultimoDiaYmString(ordenMeses[i]);
    if (ingStr > ultStr) continue;
    return i;
  }
  return ordenMeses.length;
}

function round2(x) {
  return Math.round(Number(x) * 100) / 100;
}

/** Moda única con frecuencia ≥ ceil(n/2) (monto habitual; sin empate en el máximo). */
function modaMayoritariaUnicaCts(montos) {
  const n = montos.length;
  if (n < 2) return null;
  const minMayoria = Math.ceil(n / 2);
  const counts = new Map();
  for (const m of montos) {
    const k = String(round2(Number(m) || 0));
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  let maxC = 0;
  for (const c of counts.values()) {
    if (c > maxC) maxC = c;
  }
  if (maxC < minMayoria) return null;
  const candidatos = [];
  for (const [k, c] of counts.entries()) {
    if (c === maxC) candidatos.push(Number(k));
  }
  if (candidatos.length !== 1) return null;
  return candidatos[0] > 0 ? candidatos[0] : null;
}

/** Misma regla que CtsCalculoService::baseRemuneracionTipoFijoVariable */
function baseRemuneracionTipoFijoVariable(montosDesdeIngreso) {
  const n = montosDesdeIngreso.length;
  if (n === 0) return { esFija: false, base: 0, promedio: 0 };
  const sum = montosDesdeIngreso.reduce((a, b) => a + Number(b || 0), 0);
  const promedio = sum / n;
  if (n === 1 && (montosDesdeIngreso[0] ?? 0) > 0) {
    return { esFija: true, base: round2(montosDesdeIngreso[0]), promedio: round2(promedio) };
  }
  if (n >= 3) {
    const u0 = montosDesdeIngreso[n - 3];
    const u1 = montosDesdeIngreso[n - 2];
    const u2 = montosDesdeIngreso[n - 1];
    if (u0 === u1 && u1 === u2 && u0 > 0) {
      return { esFija: true, base: round2(montosDesdeIngreso[n - 1]), promedio: round2(promedio) };
    }
  }
  const moda = modaMayoritariaUnicaCts(montosDesdeIngreso);
  if (moda != null) {
    return { esFija: true, base: round2(moda), promedio: round2(promedio) };
  }
  return { esFija: false, base: round2(promedio), promedio: round2(promedio) };
}

function sumaConceptosIncluidos(fila) {
  let s = 0;
  for (const c of fila.conceptos || []) {
    if ((c.incluir ?? true) !== false) s += Number(c.monto) || 0;
  }
  return round2(s);
}

/**
 * @param {object} params
 * @param {Array<{ mes_key: string, conceptos: Array<{ monto?: number, incluir?: boolean }> }>} params.sueldosMensuales
 * @param {string} params.periodo
 * @param {number} params.anio
 * @param {string|null|undefined} params.ultimaFechaIngresoLabores Y-m-d
 * @param {string|null|undefined} params.fechaFinSemestre Y-m-d (fin semestre CTS)
 * @param {string|null|undefined} [params.regimenCodigo] MYPP (Pequeña Empresa) aplica ×1/2 al monto CTS (igual backend).
 * @param {boolean} [params.aplicaGratificacionPagadaCts] Semestre incluye julio o diciembre (grat.)
 * @param {number|string|null} [params.gratificacionPagadaCts] Gratificación bruta pagada (manual)
 * @param {number} [params.gratificacionPagadaSugerida] Desde planilla 0406/0313 o módulo gratificaciones
 * @returns {null|object}
 */
export function calcularResumenCtsDepositoLive({
  sueldosMensuales,
  periodo,
  anio,
  ultimaFechaIngresoLabores,
  fechaFinSemestre,
  regimenCodigo,
  aplicaGratificacionPagadaCts,
  gratificacionPagadaCts,
  gratificacionPagadaSugerida = 0,
}) {
  const ordenMeses = getOrdenMesesCts(periodo, anio);
  if (!ordenMeses.length || !fechaFinSemestre) return null;

  const porMes = {};
  for (const fila of sueldosMensuales || []) {
    const k = fila.mes_key;
    if (k) porMes[k] = sumaConceptosIncluidos(fila);
  }
  const montosOrdenados = ordenMeses.map((m) => round2(porMes[m] ?? 0));
  const idx = indicePrimerMesComputoCts(ordenMeses, ultimaFechaIngresoLabores);
  if (ultimaFechaIngresoLabores && idx >= ordenMeses.length) return null;
  if (ingresoExcluyePorPoliticaDepositoCts(periodo, anio, ultimaFechaIngresoLabores)) {
    return null;
  }

  const montosDesdeIngreso = montosOrdenados.slice(idx);
  const n = montosDesdeIngreso.length;
  if (n === 0) return null;

  const tyBase = baseRemuneracionTipoFijoVariable(montosDesdeIngreso);
  const esFija = tyBase.esFija;
  const baseRemuneracion = tyBase.base;
  const aplicaGrat =
    typeof aplicaGratificacionPagadaCts === 'boolean'
      ? aplicaGratificacionPagadaCts
      : ordenMeses.some((ym) => {
          const mm = ym.slice(5, 7);
          return mm === '07' || mm === '12';
        });
  let gratParaSexto = 0;
  if (aplicaGrat) {
    const sug = Number(gratificacionPagadaSugerida) || 0;
    const rawStr =
      gratificacionPagadaCts !== null && gratificacionPagadaCts !== undefined
        ? String(gratificacionPagadaCts).trim()
        : '';
    const hasManual = rawStr !== '' && !Number.isNaN(Number(rawStr));
    if (hasManual) {
      gratParaSexto = round2(Number(rawStr));
    } else if (sug > 0) {
      gratParaSexto = round2(sug);
    }
  }
  const remuneracionComputable = round2(baseRemuneracion + gratParaSexto / 6);

  const desdeIngresoReal =
    ultimaFechaIngresoLabores != null && String(ultimaFechaIngresoLabores).trim() !== ''
      ? parseDateSafe(ultimaFechaIngresoLabores)
      : null;
  const desde =
    desdeIngresoReal && !Number.isNaN(desdeIngresoReal.getTime())
      ? desdeIngresoReal
      : parseDateSafe(`${ordenMeses[0]}-01`);
  const hasta = parseDateSafe(fechaFinSemestre);
  const tre = mesesEquivalentesCtsTrentina(desde, hasta, 6);
  const meses = tre.meses;
  const dias = tre.dias;
  const mesesEquivalentes = tre.equivalente;
  if (mesesEquivalentes <= 0) return null;

  const codigoReg = String(regimenCodigo || '').trim().toUpperCase();
  const factorMypePequeña = codigoReg === 'MYPP' ? 0.5 : 1;
  const montoCts = round2((remuneracionComputable / 12) * mesesEquivalentes * factorMypePequeña);

  return {
    promedio6Meses: tyBase.promedio,
    base_remuneracion: baseRemuneracion,
    tipo_remuneracion: esFija ? 'fija' : 'variable',
    gratificacion_estimada_semestre: 0,
    gratificacion_para_sexto: gratParaSexto,
    aporte_sexto_gratificacion: round2(gratParaSexto / 6),
    remuneracion_computable: remuneracionComputable,
    monto_cts: montoCts,
    meses_equivalentes_cts: Math.round(mesesEquivalentes * 10000) / 10000,
    meses_completos_cts: meses,
    dias_fraccion_cts: dias,
  };
}
